import React, { useEffect, useRef, useState } from 'react';
import Wrapper from './styles';
import { useFilterContext } from '../../context/filter_context';
import { useLanguageContext } from '../../context/language_context';
import GridView from '../GridView/';
import ListView from '../ListView/';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PRODUCTS_PER_PAGE = 20;

// builds a compact page list like [1, '...', 4, 5, 6, '...', 12]
const getPageNumbers = (current, total) => {
  const pages = [1];
  if (current > 3) pages.push('...');

  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }

  if (current < total - 2) pages.push('...');
  if (total > 1) pages.push(total);

  return pages;
};

const ProductList = () => {
  const { filtered_products: products, grid_view } = useFilterContext();
  const { t } = useLanguageContext();
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  // jump back to page 1 whenever the filtered/sorted list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [products]);

  if (products.length < 1) {
    return <h5>sorry, no products matched your search</h5>;
  }

  const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const currentProducts = products.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={listTopRef}>
      {grid_view === false ? (
        <ListView products={currentProducts}>product list</ListView>
      ) : (
        <GridView products={currentProducts}>product list</GridView>
      )}

      {totalPages > 1 && (
        <Wrapper>
          <nav className="pagination" aria-label="Products pagination">
            <button
              type="button"
              className="page-btn nav"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label={t.products?.previous || 'Previous'}
            >
              <ChevronLeft size={16} />
            </button>

            {getPageNumbers(currentPage, totalPages).map((page, index) =>
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="ellipsis">
                  ...
                </span>
              ) : (
                <button
                  type="button"
                  key={page}
                  className={`page-btn ${page === currentPage ? 'active' : ''}`}
                  onClick={() => goToPage(page)}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              )
            )}

            <button
              type="button"
              className="page-btn nav"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label={t.products?.next || 'Next'}
            >
              <ChevronRight size={16} />
            </button>
          </nav>
        </Wrapper>
      )}
    </div>
  );
};

export default ProductList;
